import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { FormattedMessage, useIntl } from 'react-intl'

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid2'
import { useEffect, useState } from 'react'
import { ParkingEvent } from '../../apis/webPayment/dto'
import parkingEventsApi from '../../apis/webPayment/parkingEventsApi'
import { PaymentSteps } from '../../app/paymentSteps'
import { licensePlateInputSsKey } from '../../app/storageKeys'
import { MoovyLink } from '../../components'
import useLocale from '../../hooks/useLocale'
import usePaymentStep from '../../hooks/usePaymentStep'
import localizationResources from '../../i18nResources'
import Finavia from '../../resources/finavia.png'
import { MoovyPalette, MoovyStyle } from '../../styles/theme'
import { MoovyToast } from '../../utils/moovyToast'
import PaymentProviderInformation from './paymentProviderInformation'
import { FormValues } from './stepFillLicensePlate/formLicensePlate'
import StepFillLicensePlate from './stepFillLicensePlate/stepFillLicensePlate'
import StepFinish from './stepFinish/stepFinish'
import StepInvoice from './stepInvoice/stepInvoice'

const WebPayment = () => {
  const FORM_REFERENCE = 'formWebPayment'

  const intl = useIntl()
  const { locale, setLocale } = useLocale()
  const [filledLicensePlate, setFilledLicensePlate] = useState(
    sessionStorage.getItem(licensePlateInputSsKey) ?? '',
  )
  const [invoices, setInvoices] = useState<ParkingEvent[]>([])
  useEffect(() => {
    sessionStorage.setItem(licensePlateInputSsKey, filledLicensePlate)
  }, [filledLicensePlate])
  const [currentPaymentStep, setCurrentPaymentStep] = usePaymentStep()
  const [isPageReady, setIsPageReady] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  const locOptions = localizationResources.supportedLanguages.map((key) => {
    return {
      key: key,
      text: intl.formatMessage({ id: `language.${key}` }),
    }
  })

  const fetchInvoices = async (licensePlate: string) => {
    setIsButtonDisabled(true)
    try {
      const invoicesQuery = await parkingEventsApi.getInvoices(licensePlate)
      setFilledLicensePlate(
        invoicesQuery.data.data?.licensePlate ?? licensePlate,
      )
      if ((invoicesQuery.data.data?.invoices ?? []).length > 0) {
        setInvoices(invoicesQuery.data.data?.invoices ?? [])
        setCurrentPaymentStep(PaymentSteps.INVOICE)
      } else {
        setInvoices([])
        setCurrentPaymentStep(PaymentSteps.FILL_LICENSE_PLATE_ERROR)
      }
    } catch (error) {
      setInvoices([])
      MoovyToast.showToast(
        intl.formatMessage({
          id: 'page.webPayment.stepFillLicensePlate.getInvoice.errorToast',
        }),
        'error',
      )
      setCurrentPaymentStep(PaymentSteps.FILL_LICENSE_PLATE)
    } finally {
      setIsButtonDisabled(false)
      setIsPageReady(true)
    }
  }

  // Fetch invoices on first render if we are in invoice step
  useEffect(() => {
    if (
      [PaymentSteps.INVOICE, PaymentSteps.INVOICE_ERROR].includes(
        currentPaymentStep,
      )
    ) {
      fetchInvoices(filledLicensePlate)
    } else {
      setIsPageReady(true)
    }
  }, [])

  const onSubmitLicensePlate = (values: FormValues) => {
    // Reset possible error state
    setCurrentPaymentStep(PaymentSteps.FILL_LICENSE_PLATE)
    setFilledLicensePlate(values.licensePlate)
    fetchInvoices(values.licensePlate)
  }

  const GridLanguage = () => {
    return (
      <Grid
        container
        size={12}
        style={{
          alignItems: 'center',
        }}
      >
        <Grid
          size={6}
          style={{
            alignItems: 'left',
            justifyContent: 'flex-start',
            textAlign: 'left',
          }}
        >
          {currentPaymentStep !== 'FILL_LICENSE_PLATE' &&
            [PaymentSteps.INVOICE, PaymentSteps.INVOICE_ERROR].includes(
              currentPaymentStep,
            ) && (
              <IconButton
                aria-label={intl.formatMessage({ id: 'common.button.back' })}
                onClick={() =>
                  setCurrentPaymentStep(PaymentSteps.FILL_LICENSE_PLATE)
                }
              >
                <ArrowBackRoundedIcon />
              </IconButton>
            )}
        </Grid>
        <Grid
          size={6}
          style={{
            alignItems: 'right',
            justifyContent: 'flex-end',
            textAlign: 'right',
          }}
        >
          <FormControl>
            <Select
              name="language"
              aria-label={intl.formatMessage({ id: 'language' })}
              labelId="select-language"
              value={locale}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                color: MoovyPalette.primaryMain,
              }}
              IconComponent={(props) => (
                <ExpandMoreIcon
                  {...props}
                  style={{ color: MoovyPalette.primaryMain }}
                />
              )}
            >
              {locOptions.map((item) => (
                <MenuItem
                  aria-label={item.text}
                  key={item.key}
                  value={item.key}
                  onClick={() => {
                    setLocale(item.key)
                  }}
                  disabled={item.key === locale}
                >
                  {item.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    )
  }

  const GridLogo = () => {
    return (
      <Grid
        size={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: {
            xs: MoovyStyle.elementSpacingMobile * 2,
            sm: MoovyStyle.elementSpacingDesktop * 2,
          },
        }}
      >
        <img src={Finavia} />
      </Grid>
    )
  }

  const GridCustomerService = () => {
    return (
      <>
        <Grid
          size={12}
          sx={{
            marginTop: {
              xs: MoovyStyle.elementSpacingMobile,
              sm: MoovyStyle.elementSpacingDesktop,
            },
          }}
        >
          <Divider />
        </Grid>
        <Stack paddingTop={3} spacing={2}>
          <Typography>
            <FormattedMessage id="page.webPayment.label.customerService.part1" />{' '}
            <MoovyLink.Link
              href={intl.formatMessage({
                id: 'facility.customerService.url',
              })}
              aria-label={intl.formatMessage({
                id: 'page.webPayment.label.customerService.urlTitle',
              })}
              bold
            >
              <FormattedMessage id="page.webPayment.label.customerService.part2" />
            </MoovyLink.Link>
            .
          </Typography>

          <Typography>
            <MoovyLink.Link
              href={intl.formatMessage({ id: 'facility.privacyPolicy.url' })}
              target="_blank"
              bold
            >
              <FormattedMessage id="facility.privacyPolicy.text" />
            </MoovyLink.Link>
          </Typography>
        </Stack>
      </>
    )
  }

  const PaymentServiceProviderInformation = () => {
    const isVisible = [
      PaymentSteps.FILL_LICENSE_PLATE,
      PaymentSteps.FILL_LICENSE_PLATE_ERROR,
      PaymentSteps.INVOICE,
      PaymentSteps.INVOICE_ERROR,
    ].includes(currentPaymentStep)
    if (!isVisible) {
      return null
    }

    return <PaymentProviderInformation />
  }

  const RenderStep = () => {
    if (!isPageReady) {
      return (
        <Box
          marginTop={6}
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <CircularProgress />
        </Box>
      )
    }
    switch (currentPaymentStep) {
      case 'INVOICE':
      case 'INVOICE_ERROR':
        return (
          <StepInvoice licensePlate={filledLicensePlate} invoices={invoices} />
        )
      case 'FINISH':
      case 'FINISH_ERROR':
        return <StepFinish />
      case 'FILL_LICENSE_PLATE':
      case 'FILL_LICENSE_PLATE_ERROR':
      default:
        return (
          <StepFillLicensePlate
            formReference={FORM_REFERENCE}
            licensePlate={filledLicensePlate}
            isButtonDisabled={isButtonDisabled}
            onSubmitLicensePlate={onSubmitLicensePlate}
          />
        )
    }
  }

  return (
    <>
      <Grid
        container
        style={{
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <GridLanguage />
        <GridLogo />
        <RenderStep />
        <PaymentServiceProviderInformation />
        <GridCustomerService />
      </Grid>
    </>
  )
}

export default WebPayment
